import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TableSortLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { makeApiCall } from '../../config/api';

const ContractServicesReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('service_type');
  const [sortDirection, setSortDirection] = useState('asc');

  // Available service types based on the database
  const serviceTypes = [
    { value: '', label: t('reports.contractServices.allServices') },
    { value: 'lawn', label: t('reports.contractServices.serviceTypes.lawn') },
    { value: 'gardening', label: t('reports.contractServices.serviceTypes.gardening') },
    { value: 'spring', label: t('reports.contractServices.serviceTypes.spring') },
    { value: 'fall', label: t('reports.contractServices.serviceTypes.fall') },
    { value: 'trimming', label: t('reports.contractServices.serviceTypes.trimming') },
    { value: 'turf', label: t('reports.contractServices.serviceTypes.turf') },
    { value: 'snow', label: t('reports.contractServices.serviceTypes.snow') },
    { value: 'construction', label: t('reports.contractServices.serviceTypes.construction') }
  ];

  const sortOptions = [
    { value: 'service_type', label: t('reports.contractServices.sortBy.serviceType') },
    { value: 'quote_number', label: t('reports.contractServices.sortBy.quoteNumber') },
    { value: 'company_name', label: t('reports.contractServices.sortBy.customer') },
    { value: 'created_at', label: t('reports.contractServices.sortBy.createdDate') }
  ];

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (serviceTypeFilter) params.append('service_type', serviceTypeFilter);
      if (sortBy) params.append('sort_by', sortBy);

      const response = await makeApiCall(`api/reports/contract-services?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching contract services report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const response = await makeApiCall('api/reports/contract-services/pdf', {
        method: 'POST',
        body: JSON.stringify({
          service_type: serviceTypeFilter || null,
          sort_by: sortBy
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `contract-services-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatServiceType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Get unique service types for filtering display
  const allServiceTypes = new Set();
  data.forEach(contract => {
    contract.services.forEach(service => {
      allServiceTypes.add(service.service_type);
    });
  });

  const totalServices = data.reduce((sum, contract) => sum + contract.services.length, 0);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('reports.contractServices.filterByServiceType')}</InputLabel>
              <Select
                value={serviceTypeFilter}
                onChange={(e) => setServiceTypeFilter(e.target.value)}
                label={t('reports.contractServices.filterByServiceType')}
              >
                {serviceTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('reports.contractServices.sortByLabel')}</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label={t('reports.contractServices.sortByLabel')}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading}
                sx={{ bgcolor: '#2e7d32' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
              >
                {t('reports.downloadPDF')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#fff3e0' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('reports.summary')}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`${t('reports.contractServices.totalContracts')}: ${data.length}`} color="primary" />
              <Chip label={`${t('reports.contractServices.totalServices')}: ${totalServices}`} color="secondary" />
              {serviceTypeFilter && (
                <Chip
                  label={`${t('reports.contractServices.filteredBy')}: ${serviceTypes.find(t => t.value === serviceTypeFilter)?.label || serviceTypeFilter}`}
                  color="info"
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('reports.contractServices.noContractsFound')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {data.map((contract, idx) => (
            <Card key={contract.quote.id || idx} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f57c00' }}>
                  {t('reports.contractServices.contract')}: {contract.quote.quote_number}
                  {contract.quote.quote_reference && ` - ${contract.quote.quote_reference}`}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('reports.customer')}:</strong>{' '}
                      {contract.customer.company_name ||
                        `${contract.customer.first_name} ${contract.customer.last_name}`}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('reports.contractServices.address')}:</strong>{' '}
                      {contract.customer.street_address || ''}, {contract.customer.city || ''}{' '}
                      {contract.customer.postal_code || ''}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('reports.contractServices.contractDates')}:</strong>{' '}
                      {formatDate(contract.quote.contract_start_date)} -{' '}
                      {formatDate(contract.quote.contract_end_date)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('reports.contractServices.totalAmount')}:</strong>{' '}
                      ${parseFloat(contract.quote.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', mt: 2 }}>
                  {t('reports.contractServices.services')} ({contract.services.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'service_type'}
                            direction={sortBy === 'service_type' ? sortDirection : 'asc'}
                            onClick={() => handleSort('service_type')}
                          >
                            <strong>{t('reports.contractServices.serviceType')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell><strong>{t('reports.contractServices.createdDate')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contract.services
                        .sort((a, b) => {
                          if (sortBy === 'service_type') {
                            return sortDirection === 'asc'
                              ? a.service_type.localeCompare(b.service_type)
                              : b.service_type.localeCompare(a.service_type);
                          }
                          return 0;
                        })
                        .map((service, serviceIdx) => (
                          <TableRow key={serviceIdx}>
                            <TableCell>
                              <Chip
                                label={formatServiceType(service.service_type)}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>{formatDate(service.created_at)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ContractServicesReport;

